import React, { useRef } from 'react';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';

// WhiteGreenDot component
const WhiteGreenDot = ({ size = "18px", dotColor = "#8cfc42" }) => {
  return (
    <div 
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        backgroundColor: 'white',
        borderRadius: '50%'
      }}
    >
      <div 
        style={{
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '66%',
          height: '66%',
          backgroundColor: dotColor,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    </div>
  );
};

// ModelText component
const ModelText = ({ text, variant = "right", dotColor = "##8cfc42", dotSize = "12px" }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: variant === 'right' ? 'row' : 'row-reverse',
      minWidth: '20vw',
      alignItems: 'center'
    }}>
      <div 
        style={{
          borderRadius: '50%',
          minWidth: dotSize,
          minHeight: dotSize,
          backgroundColor: dotColor,
          margin: 'auto 0',
          flexShrink: 0
        }}
      />
      <div 
        className="model-text"
        style={{
          position: 'relative',
          display: 'flex',
          fontSize: '18px',
          fontWeight: '600',
          color: '#425a70',
          overflowWrap: 'normal',
          zIndex: 1,
          paddingLeft: variant !== 'left' ? '10px' : '0',
          paddingRight: variant !== 'right' ? '10px' : '0',
          whiteSpace: 'nowrap'
        }}
      >
        {text}
      </div>
    </div>
  );
};

export const Spherical = ({
  core,
  coreModification,
  coreColor,
  surfaceFunctionalization,
  surfaceFunctionalizationColor,
  lineColor = "#8cfc42",
  baseRotation = [0, 0, 0]
}) => {
  const group = useRef(null);
  const modelGroup = useRef(null);
  const mainSpherical = useRef(null);
  const haloSpherical = useRef(null);

  // Create sphere geometries
  const mainSphereGeometry = new THREE.SphereGeometry(1.4, 32, 32);
  const haloSphereGeometry = new THREE.SphereGeometry(1.6, 32, 32);

  return (
    <group ref={group}>
      {/* Core label */}
      {Boolean(core) && (
        <group position={[0, 0, 0]}>
          <Html className="core" position={[1.4, 1.15, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={coreModification ? `${core?.name}/${coreModification}` : (core?.name || 'Core')}
              variant="right"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[0, 0, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [0, 0, 2],
              [1, 1.15, 2],
              [1.4, 1.15, 2],
            ]}
            lineWidth={3}
            color={lineColor}
          />
        </group>
      )}

      {/* Surface functionalization label */}
      {Boolean(surfaceFunctionalization) && (
        <group position={[0, 0, 0]}>
          <Html className="core--left" position={[-1.4, 0.9, 2]} zIndexRange={[0, 1]}>
            <ModelText 
              dotSize="12px" 
              text={surfaceFunctionalization} 
              variant="left" 
              dotColor={lineColor} 
            />
          </Html>
          <Html className="center" position={[-0.72, 0.6, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [-0.72, 0.6, 2],
              [-1.1, 0.9, 2],
              [-1.4, 0.9, 2],
            ]}
            lineWidth={1.5}
            color={lineColor}
          />
        </group>
      )}

      {/* 3D Model */}
      <group ref={modelGroup} position={[0, 0, 0]} rotation={baseRotation}>
        {/* Main sphere */}
        <mesh geometry={mainSphereGeometry} ref={mainSpherical}>
          <meshStandardMaterial 
            roughness={0} 
            metalness={0.1} 
            color={coreColor} 
          />
        </mesh>

        {/* Halo sphere */}
        {Boolean(surfaceFunctionalization) && (
          <mesh ref={haloSpherical} geometry={haloSphereGeometry}>
            <meshStandardMaterial 
              transparent 
              opacity={0.35} 
              color={surfaceFunctionalizationColor} 
            />
          </mesh>
        )}
      </group>
    </group>
  );
};